import { Dropdown } from "primereact/dropdown";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { getQuotationAPI } from "../../../redux/homeSlice";
import { AppDispatch } from "../../../redux/store";
import { IsStringNullEmptyOrUndefined } from "../../../utils/nullCheck";
import { DECIMAL_PATTERN } from "../../../utils/pattern";
import {
  IsFormValid,
  removeSpaceOnFirstCharacter,
} from "../../../utils/shared.service";

function GetQuotation() {
  const location = useLocation();

  const { systemName, uuid } = location.state || {};

  const dispatch = useDispatch<AppDispatch>();

  const [price, setPrice] = useState("");

  const [isQuotationFormSubmitted, setIsQuotationFormSubmitted] =
    useState<boolean>(false);

  const [quotationForm, setQuotationForm] = useState({
    name: "",
    height: "",
    width: "",
  });

  const [quotationFormErrors, setQuotationFormErrors] = useState({
    name: "Please select system",
    height: "Please enter height",
    width: "Please enter width ",
  });

  const systems = [
    { name: "WALTZ.CLOSE" },
    { name: "WALTZ.GLIDE" },
    { name: "WALTZ.SLIDE" },
    { name: "WALTZ.SWING" },
  ];

  useEffect(() => {
    if (systemName) {
      setQuotationForm((prevForm) => ({
        ...prevForm,
        name: systemName,
      }));
      setQuotationFormErrors((prevForm) => ({
        ...prevForm,
        name: "",
      }));
    }
  }, [systemName]);

  const validateQuotationForm = (fieldName: string, value: any) => {
    switch (fieldName) {
      case "name": {
        setQuotationFormErrors({
          ...quotationFormErrors,
          [fieldName]: IsStringNullEmptyOrUndefined(value)
            ? "Please select system"
            : "",
        });
        break;
      }

      case "width": {
        const isValidWidth: boolean =
          !IsStringNullEmptyOrUndefined(value) &&
          DECIMAL_PATTERN.test(value) &&
          10000 >= parseFloat(value) &&
          parseFloat(value) >= 1;

        setQuotationFormErrors({
          ...quotationFormErrors,
          [fieldName]: isValidWidth
            ? ""
            : IsStringNullEmptyOrUndefined(value)
              ? "Please enter width"
              : `Width should be from ${1}mm to ${10000}mm`,
        });

        if (parseFloat(value) > 10000) {
          value = "";
        }

        break;
      }

      case "height": {
        const isValidWidth: boolean =
          !IsStringNullEmptyOrUndefined(value) &&
          DECIMAL_PATTERN.test(value) &&
          10000 >= parseFloat(value) &&
          parseFloat(value) >= 1;

        setQuotationFormErrors({
          ...quotationFormErrors,
          [fieldName]: isValidWidth
            ? ""
            : IsStringNullEmptyOrUndefined(value)
              ? "Please enter height"
              : `Height should be from ${1}mm to ${3600}mm`,
        });

        if (parseFloat(value) > 3600) {
          value = "";
        }

        break;
      }
      default:
        break;
    }
    setQuotationForm({
      ...quotationForm,
      [fieldName]: value,
    });
  };

  const getQuotation = async () => {
    setIsQuotationFormSubmitted(true);
    const isValid = IsFormValid(quotationFormErrors);
    if (isValid) {
      setIsQuotationFormSubmitted(false);
    }

    const reqobj = {
      uuid: uuid,
      name: quotationForm.name,
      height: quotationForm.height,
      width: quotationForm.width,
    };

    try {
      const result = await dispatch(getQuotationAPI(reqobj));
      if (result.payload) {
        setPrice(result.payload.data.price);
      }
    } catch (error) { }
  };

  const clearAll = () => {
    setQuotationForm({
      ...quotationForm,
      height: "",
      width: "",
    });
    setPrice("");
  };

  return (
    <div>
      <section className="innerBannerWrapper">
        <img src="/assets/images/quotation-banner.webp" alt="" />
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="txt-white titleMain text-center">GET QUOTE NOW</h2>
            </div>
          </div>
        </div>
      </section>

      <section className="quotationWrapper py-100">
        <div className="container">
          <div className="row">
            <div className="col-12 mb-5">
              <h2>GET QUOTE NOW</h2>
            </div>
            <div className="col-lg-4 col-md-4 col-sm-12 col-12 mb-4">
              <div className="form-group w-100">
                <label className="form-label">
                  <b>System</b>
                  <sup className="mandatory">*</sup>
                </label>
                <Dropdown
                  className="w-100"
                  value={quotationForm.name}
                  onChange={(e) => validateQuotationForm("name", e.value)}
                  options={systems}
                  optionLabel="name"
                  optionValue="name"
                  placeholder="Select a System Name"
                  disabled={price ? true : false}
                />
                {isQuotationFormSubmitted && (
                  <small className="text-danger font-weight-500">
                    {quotationFormErrors.name}
                  </small>
                )}
              </div>
            </div>
            <div className="col-lg-8 col-md-8 col-sm-12 col-12 mb-4 sizeHldr">
              <div className="name">
                <b>Size:</b>
              </div>
              <div className="form-group w-100">
                <label className="form-label">
                  Width(mm)<sup className="mandatory">*</sup>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Width"
                  name="width"
                  value={quotationForm.width}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, "");
                    validateQuotationForm(e.target.name, value);
                  }}
                  onKeyDown={(event: any) => {
                    removeSpaceOnFirstCharacter(event);
                  }}
                  disabled={price ? true : false}
                />
                <small className="d-block w-100 mt-2">
                  <b>Info:</b> Width should be up to 10000 mm
                </small>
                {isQuotationFormSubmitted && (
                  <small className="text-danger font-weight-500">
                    {quotationFormErrors.width}
                  </small>
                )}
              </div>
              <div className="form-group w-100">
                <label className="form-label">
                  Height(mm)<sup className="mandatory">*</sup>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Height"
                  name="height"
                  value={quotationForm.height}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, "");
                    validateQuotationForm(e.target.name, value);
                  }}
                  onKeyDown={(event: any) => {
                    removeSpaceOnFirstCharacter(event);
                  }}
                  disabled={price ? true : false}
                />
                <small className="d-block w-100 mt-2">
                  <b>Info:</b> Height should be up to 3600 mm
                </small>
                {isQuotationFormSubmitted && (
                  <small className="text-danger font-weight-500">
                    {quotationFormErrors.height}
                  </small>
                )}
              </div>
              {!price ? (
                <button
                  type="button"
                  className="btn btn-black-border1"
                  onClick={() => getQuotation()}
                  disabled={
                    quotationForm.width && quotationForm.height ? false : true
                  }
                >
                  GET
                </button>
              ) : (
                <button
                  className="btn btn-black-border1"
                  onClick={() => clearAll()}
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="col-lg-4 col-md-4 col-sm-12 col-12 mb-4">
              <img src="assets/images/cost-image.webp" alt=""></img>
            </div>
            <div className="col-lg-8 col-md-8 col-sm-12 col-12 mb-4">
              <div className="form-group">
                <label className="form-label">
                  <b>Quote</b>
                </label>
                <textarea
                  className="form-control"
                  id="exampleFormControlTextarea1"
                  rows={2}
                  disabled
                  value={price}
                ></textarea>
              </div>
              <p className="txt-gray mt-2">
                *Prices are subject to change upon final selection of system,
                skin finish and dimensions.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default GetQuotation;
